/**
 * AvatarContext - Shared avatar state across all pages
 * Provides avatar picker modal visibility, current user avatar, and update function
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useUserProfile } from '../../shared/contexts/UserProfileContext';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { updateUserAvatar, updateUserAvatarConfig } from '../helpers/avatarsApi';
import { isMemberRowQueryKey } from '../../competitions/helpers/memberRowInvalidation.helper';
import { AVATAR_IDS, type AvatarId } from '../../../constants/avatarIds';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import { logger } from '../../helpers/logger.helper';

interface AvatarContextValue {
  currentAvatarId: AvatarId | null;
  /** Current custom layered look; non-null overrides the ready avatarId. */
  currentAvatarConfig: EquippedMap | null;
  isLoadingAvatar: boolean;
  /** Save a ready avatar (clears any custom look). */
  updateAvatar: (avatarId: AvatarId, onSuccess?: () => void) => Promise<void>;
  /** Save a custom layered look. */
  updateAvatarConfig: (avatarConfig: EquippedMap, onSuccess?: () => void) => Promise<void>;
  error: Error | null;
}

const AvatarContext = createContext<AvatarContextValue | undefined>(undefined);

interface AvatarProviderProps {
  children: React.ReactNode;
}

export const AvatarProvider: React.FC<AvatarProviderProps> = ({ children }) => {
  const [error, setError] = useState<Error | null>(null);
  const [isUpdatingAvatar, setIsUpdatingAvatar] = useState(false);
  const { userId } = useSupabaseAuth();
  const { profile, isLoading: isLoadingProfile, updateProfile } = useUserProfile();
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  const queryClient = useQueryClient();
  const { showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();

  apiClientRef.current = apiClient;

  // Derive avatar from shared profile (single GET profile used by avatar + favorite leagues)
  const currentAvatarId: AvatarId | null = !userId
    ? null
    : (profile?.avatarId ?? AVATAR_IDS.BLUE_WITH_HAIR);
  const currentAvatarConfig: EquippedMap | null = !userId ? null : (profile?.avatarConfig ?? null);
  const isLoadingAvatar = (!!userId && isLoadingProfile) || isUpdatingAvatar;

  const updateAvatar = useCallback(
    async (avatarId: AvatarId, onSuccess?: () => void) => {
      logger.info('[AvatarContext.updateAvatar] Avatar update initiated', {
        userId,
        avatarId,
        hasApiClient: !!apiClientRef.current,
        isApiClientFunction: typeof apiClientRef.current === 'function',
      });

      if (!userId) {
        const error = new Error('User not signed in');
        logger.error('[AvatarContext.updateAvatar] User not signed in', {
          avatarId,
        });
        setError(error);
        await showAlert({ title: 'Error', message: 'You must be signed in to update your avatar.' });
        return;
      }

      setIsUpdatingAvatar(true);
      setError(null);

      try {
        logger.info('[AvatarContext.updateAvatar] Calling updateUserAvatar API', {
          userId,
          avatarId,
        });

        // Call API and use the returned avatarId from server response
        const updatedAvatarId = await updateUserAvatar(userId, avatarId, apiClientRef.current as (endpoint: string, config?: unknown) => Promise<import('axios').AxiosResponse>);

        logger.info('[AvatarContext.updateAvatar] API call completed', {
          userId,
          requestedAvatarId: avatarId,
          returnedAvatarId: updatedAvatarId,
          isNull: updatedAvatarId === null,
          isUndefined: updatedAvatarId === undefined,
        });

        // Explicit null check: if API returns null, do not update profile cache
        if (updatedAvatarId === null || updatedAvatarId === undefined) {
          logger.error('[AvatarContext.updateAvatar] API returned null/undefined avatarId', {
            userId,
            requestedAvatarId: avatarId,
            returnedAvatarId: updatedAvatarId,
          });
          throw new Error('Failed to update avatar. Server did not return updated avatarId.');
        }

        logger.info('[AvatarContext.updateAvatar] Updating state with returned avatarId', {
          userId,
          requestedAvatarId: avatarId,
          returnedAvatarId: updatedAvatarId,
        });

        // Update shared profile cache; clear any custom look so the ready avatar wins.
        updateProfile({ avatarId: updatedAvatarId, avatarConfig: null });
        // Every board and profile that draws this user's row now holds a stale look.
        void queryClient.invalidateQueries({
          predicate: (query) => isMemberRowQueryKey(query.queryKey as readonly unknown[]),
        });
        showSnackbar('Avatar updated!', 'success');

        logger.info('[AvatarContext.updateAvatar] Avatar update successful', {
          userId,
          finalAvatarId: updatedAvatarId,
          hasOnSuccess: !!onSuccess,
        });

        // Call onSuccess callback after successful update (e.g., for onboarding navigation)
        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update avatar');
        const errorMessage = err instanceof Error ? err.message : String(err);
        logger.error('[AvatarContext.updateAvatar] Error during avatar update', {
          userId,
          avatarId,
          errorMessage,
          errorStack: err instanceof Error ? err.stack : undefined,
        });
        setError(error);
        showSnackbar('Failed to update avatar. Please try again.', 'error');
      } finally {
        setIsUpdatingAvatar(false);
        logger.info('[AvatarContext.updateAvatar] Avatar update flow completed', {
          userId,
          avatarId,
        });
      }
    },
    [userId, showAlert, showSnackbar, updateProfile, queryClient]
  );

  const updateAvatarConfig = useCallback(
    async (avatarConfig: EquippedMap, onSuccess?: () => void) => {
      if (!userId) {
        setError(new Error('User not signed in'));
        await showAlert({ title: 'Error', message: 'You must be signed in to update your avatar.' });
        return;
      }
      setIsUpdatingAvatar(true);
      setError(null);
      try {
        const saved = await updateUserAvatarConfig(
          userId,
          avatarConfig,
          apiClientRef.current as (endpoint: string, config?: unknown) => Promise<import('axios').AxiosResponse>
        );
        if (saved === null) {
          throw new Error('Failed to update avatar. Server did not confirm the custom look.');
        }
        // Custom look wins over the ready avatarId at render time.
        updateProfile({ avatarConfig: saved });
        void queryClient.invalidateQueries({
          predicate: (query) => isMemberRowQueryKey(query.queryKey as readonly unknown[]),
        });
        showSnackbar('Avatar updated!', 'success');
        if (onSuccess) onSuccess();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update avatar');
        logger.error('[AvatarContext.updateAvatarConfig] Error during custom avatar update', {
          userId,
          errorMessage: error.message,
        });
        setError(error);
        showSnackbar('Failed to update avatar. Please try again.', 'error');
      } finally {
        setIsUpdatingAvatar(false);
      }
    },
    [userId, showAlert, showSnackbar, updateProfile, queryClient]
  );

  const value: AvatarContextValue = {
    currentAvatarId,
    currentAvatarConfig,
    isLoadingAvatar,
    updateAvatar,
    updateAvatarConfig,
    error,
  };

  return <AvatarContext.Provider value={value}>{children}</AvatarContext.Provider>;
};

export const useAvatarContext = (): AvatarContextValue => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error('useAvatarContext must be used within AvatarProvider');
  }
  return context;
};

